import { KeywordAutoSuggest } from '@seek/discover-ui';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
} from 'react';

import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import {
  useFeatureFlagRefineBarV2Experiment,
  useSavedRecentSearchesFeatureFlag,
} from 'src/store/featureFlags/hooks.ts';
import { useDispatch, useSelector } from 'src/store/react';
import { clearDynamicPills, clearResults } from 'src/store/results';
import {
  updateKeywordsField as _updateKeywordsField,
  updateBaseKeywordParam,
} from 'src/store/search';
import { selectBaseKeywords } from 'src/store/search/selectors';
import {
  selectAuthenticated,
  selectFeatureFlag,
  selectIsResultsLoading,
  selectIsSrp,
  selectKeywordField,
  selectSearchQuery,
} from 'src/store/selectors';
import { useMelwaysCountry } from 'src/utils/melwaysHelper';

import { DismissableTooltip } from '../../DismissableTooltip/DismissableTooltip';
import { useTooltipLocalStorage } from '../../DismissableTooltip/useTooltipLocalStorage';
import { getRefineSearchQuery } from '../../utils/utils';
import {
  SavedRecentSearches,
  type SavedRecentSearchesHandle,
} from '../SavedRecentSearches/SavedRecentSearches';

import translations from './.vocab';

export const KEYWORD_INPUT_FIELD_ID = 'keywords-input';

interface KeywordFieldProps {
  onSuggestionSelected: () => void;
  onInputBlur: () => void;
  onClear: () => void;
  onReturn?: () => void;
}
const KeywordField = ({
  onSuggestionSelected,
  onInputBlur,
  onClear,
  onReturn,
}: KeywordFieldProps) => {
  const isLoading = useSelector(selectIsResultsLoading);
  const analyticsFacade = useAnalyticsFacade();
  const dispatch = useDispatch();

  const keywordsField = useSelector(selectKeywordField);
  const { dynamicPillsV2, refineBarV2 } = useFeatureFlagRefineBarV2Experiment();
  const query = getRefineSearchQuery(useSelector(selectSearchQuery));
  const country = useMelwaysCountry();
  const [keywordsFieldLocalState, setKeywordFieldLocalState] =
    useState(keywordsField);
  const hubble = useHubble();
  const visitorId = hubble.visitorId();
  const isKeywordAutosuggestV2Enabled = useSelector(
    selectFeatureFlag('keywordAutosuggestV2'),
  );
  const showDynamicPills = refineBarV2 || dynamicPillsV2;

  const baseKeywords = useSelector(selectBaseKeywords);
  const isSRP = useSelector(selectIsSrp);
  const [displaySavedRecentSearches, setDisplaySavedRecentSearches] =
    useState(false);
  const showSavedRecentInSearch = useSavedRecentSearchesFeatureFlag();
  const { isTooltipDismissed, dismissTooltip } = useTooltipLocalStorage(
    'savedRecentSearchesTooltipDismissed',
  );

  const [showTooltip, setShowTooltip] = useState(false);
  const [animateTooltip, setAnimateTooltip] = useState(false);

  useEffect(() => {
    if (ENV.CLIENT) {
      setShowTooltip(!isTooltipDismissed());
      setAnimateTooltip(true);
    }
  }, [isTooltipDismissed]);

  const handleCloseTooltip = () => {
    setShowTooltip(false);
    dismissTooltip();
  };

  useEffect(() => {
    if (!isSRP) {
      dispatch(updateBaseKeywordParam(''));
      dispatch(clearDynamicPills());
    } else if (keywordsField && !baseKeywords) {
      dispatch(updateBaseKeywordParam(keywordsField));
    }
  }, [baseKeywords, dispatch, isSRP, keywordsField]);

  useEffect(() => {
    if (keywordsField !== keywordsFieldLocalState) {
      setKeywordFieldLocalState(keywordsField);
    }
  }, [keywordsField, keywordsFieldLocalState]);

  const updateKeywordsField = useCallback(
    (text: string) => {
      setKeywordFieldLocalState(text);
      dispatch(_updateKeywordsField(text));

      if (showDynamicPills) {
        dispatch(updateBaseKeywordParam(text));
      }

      if (showSavedRecentInSearch) {
        setDisplaySavedRecentSearches(!text);
      }
    },
    [
      dispatch,
      showDynamicPills,
      setDisplaySavedRecentSearches,
      showSavedRecentInSearch,
    ],
  );

  const onKeywordsChange = useCallback(
    (
      ...args: Parameters<ComponentProps<typeof KeywordAutoSuggest>['onChange']>
    ) => {
      const [suggestion] = args;

      analyticsFacade.keywordChanged(
        suggestion.text,
        suggestion.isSuggestion,
        suggestion.previousText ?? '',
        suggestion.keywordListCount,
        suggestion.keywordRank,
        suggestion.suggestionMetadata,
      );
      updateKeywordsField(suggestion.text);

      if (suggestion.isSuggestion) {
        onSuggestionSelected();
      }
    },
    [analyticsFacade, onSuggestionSelected, updateKeywordsField],
  );

  const savedRecentSearchesClicked = useRef(false);
  const savedRecentSearchesRef = useRef<HTMLDivElement>(null);
  const onKeywordsBlur = () => {
    if (showSavedRecentInSearch) {
      // We're adding a small timeout so we can check if the user clicked into the saved/recent menu so we don't close it automatically
      setTimeout(() => {
        if (savedRecentSearchesClicked.current) {
          return;
        }

        const { keywords = '' } = query;

        if (keywordsField !== keywords) {
          updateKeywordsField(keywordsField);
          onInputBlur();
        }

        setDisplaySavedRecentSearches(false);
      }, 150);
    } else {
      const { keywords = '' } = query;

      if (keywordsField !== keywords) {
        updateKeywordsField(keywordsField);
        onInputBlur();
      }
    }
  };

  const onKeywordsClear = () => {
    dispatch(clearResults());
    updateKeywordsField('');
    onClear();
  };

  const onKeywordsFocus = () => {
    if (showSavedRecentInSearch) {
      // If the user clicked into the saved/recent menu, close tooltip
      handleCloseTooltip();
      setDisplaySavedRecentSearches(!keywordsFieldLocalState);
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (showSavedRecentInSearch && displaySavedRecentSearches) {
      const targetKey = event.key;

      switch (targetKey) {
        case 'ArrowDown':
          savedRecentRef.current?.selectNext();
          event.preventDefault(); // Prevent the page from scrolling
          break;
        case 'ArrowUp':
          savedRecentRef.current?.selectPrevious();
          event.preventDefault(); // Prevent the page from scrolling
          break;
        case 'Enter':
          savedRecentRef.current?.selectItem();
          event.preventDefault(); // Prevent the submit event from firing
          setDisplaySavedRecentSearches(false);
          break;
        case 'Escape':
          setDisplaySavedRecentSearches(false);
          break;
      }
    } else if (onReturn && event.key === 'Enter') {
      event.stopPropagation();
      event.preventDefault();

      onReturn();
    }
  };

  const { t } = useTranslations(translations);

  const shouldShowSavedRecentTooltip =
    !isLoading && showSavedRecentInSearch && !isSRP && showTooltip;

  const authenticated = useSelector(selectAuthenticated);

  const messageDisplay = authenticated
    ? t('Click here to see your recent & saved searches')
    : t('Click here to see your recent searches');

  const savedRecentRef = useRef<SavedRecentSearchesHandle | null>(null);
  return (
    <Box
      role="region"
      aria-label={t('Enter Keywords')}
      data-automation="searchKeywordsField"
      onKeyDown={onKeyDown}
      position="relative"
    >
      <KeywordAutoSuggest
        keywordAutosuggestV2={isKeywordAutosuggestV2Enabled}
        visitorId={visitorId}
        keyword={{ text: keywordsFieldLocalState }}
        id={KEYWORD_INPUT_FIELD_ID}
        onChange={onKeywordsChange}
        onBlur={onKeywordsBlur}
        onClear={onKeywordsClear}
        onFocus={onKeywordsFocus}
        country={country}
        label={t('What')}
        placeholder={t('Enter Keywords')}
        reserveMessageSpace={false}
        showMobileBackdrop={true}
        name="keywords"
      />
      {showSavedRecentInSearch && displaySavedRecentSearches ? (
        <Box
          ref={savedRecentSearchesRef}
          onBlur={() => {
            savedRecentSearchesClicked.current = false;
          }}
          onClick={() => {
            savedRecentSearchesClicked.current = true;
          }}
        >
          <SavedRecentSearches ref={savedRecentRef} />
        </Box>
      ) : null}
      {shouldShowSavedRecentTooltip && (
        <DismissableTooltip
          handleClose={handleCloseTooltip}
          message={messageDisplay}
          animateTooltip={animateTooltip}
        />
      )}
    </Box>
  );
};

export default KeywordField;
